import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/axios";
import { authReturnTo, rememberReturnTo } from "../../utils/authReturnTo";
import ProvinceSelect from "../Common/ProvinceSelect";
import { auth0Config, isAuth0Enabled } from "../../auth/auth0Config";

function Auth0SignupButton({ returnTo }) {
  const { loginWithRedirect } = useAuth0();

  const handleAuth0Signup = () => {
    rememberReturnTo(returnTo);
    loginWithRedirect({
      authorizationParams: {
        audience: auth0Config.audience,
        redirect_uri: window.location.origin,
        screen_hint: "signup"
      },
      appState: { returnTo }
    });
  };

  return (
    <Button type="button" className="btn-cardora w-100" onClick={handleAuth0Signup}>
      Crear cuenta con Auth0
    </Button>
  );
}

function RegisterForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteType = searchParams.get("type");
  const returnTo = authReturnTo(searchParams.get("returnTo"), inviteType === "business" ? "/stores/register" : "/binder");
  const invitedEmail = searchParams.get("email") || "";
  const [form, setForm] = useState({
    displayName: "",
    handle: "",
    email: invitedEmail,
    password: "",
    city: "Madrid",
    province: "Madrid",
    postalCode: "",
    country: "España"
  });
  const [preferences, setPreferences] = useState({
    pokemon: true,
    binder: true,
    trades: true,
    tradeDays: true,
    storeEvents: true,
    cardLanguages: ['es']
  });
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [acceptedUpdates, setAcceptedUpdates] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [handleStatus, setHandleStatus] = useState("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const handle = form.handle.trim();
    if (!handle) {
      setHandleStatus("idle");
      return undefined;
    }
    if (!/^[a-zA-Z0-9_.-]{3,40}$/.test(handle)) {
      setHandleStatus("invalid");
      return undefined;
    }

    let cancelled = false;
    setHandleStatus("checking");
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/handle-availability", { params: { handle } });
        if (!cancelled) setHandleStatus(data.available ? "available" : "taken");
      } catch (requestError) {
        if (!cancelled) setHandleStatus("error");
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.handle]);

  const canContinueStepOne = form.displayName.trim().length >= 2
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    && (!form.handle || handleStatus === "available")
    && form.password.length >= 8 && new TextEncoder().encode(form.password).length <= 72
    && form.password === confirmPassword;
  const canContinueStepTwo = Boolean(form.city.trim() && (!form.postalCode || /^[0-9]{5}$/.test(form.postalCode)));
  const goToStep = next => {
    if (isSubmitting) return;
    if (next > 1 && !canContinueStepOne) {
      setStep(1); setError('Revisa los datos de cuenta y confirma la contraseña.'); return;
    }
    if (next > 2 && !canContinueStepTwo) {
      setStep(2); setError('Indica tu ciudad o municipio. Si añades código postal, debe tener 5 dígitos.'); return;
    }
    setError(''); setStep(next);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (isSubmitting) return;
    if (!canContinueStepOne) { goToStep(2); return; }
    if (!canContinueStepTwo) { goToStep(3); return; }
    if (step < 3) { goToStep(step + 1); return; }
    setError("");

    if (!acceptedLegal) {
      setError("Debes aceptar los términos y la política de privacidad para crear la cuenta.");
      return;
    }
    if (form.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", {
        ...form,
        email: form.email.trim(),
        preferences,
        acceptedLegal,
        acceptedUpdates,
        returnTo
      });
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      rememberReturnTo(returnTo);
      navigate("/verify-email?sent=1&returnTo=" + encodeURIComponent(returnTo));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No pudimos crear la cuenta.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePreference = key => {
    setPreferences(current => ({ ...current, [key]: !current[key] }));
  };

  const stepTitle = useMemo(() => {
    if (step === 1) return "Cuenta";
    if (step === 2) return "Preferencias";
    return "Consentimiento";
  }, [step]);


  return (
    <Container className="page-band">
      <Row className="justify-content-center">
        <Col md={9} lg={7}>
          <Card className="cardora-card">
            <Card.Body>
              <div className="auth-register-heading">
                <span className="auth-register-eyebrow">Únete a Cardora</span>
                <h1 className="section-title mb-1">{inviteType === "business" ? "Registrar tienda" : "Crear cuenta"}</h1>
                <p>{inviteType === "business" ? "Crea tu cuenta para completar el registro de tu tienda." : "Prepara tu espacio para coleccionar, descubrir y conectar."}</p>
              </div>
              <div className="auth-step-summary">
                <span>Paso {step} de 3</span>
                <strong>{stepTitle}</strong>
              </div>
              <div className="auth-stepper mb-4" aria-label="Progreso del registro">
                {[1, 2, 3].map(item => (
                  <button type="button" key={item} aria-label={`Paso ${item}`} aria-current={step === item ? 'step' : undefined} disabled={isSubmitting} className={step === item ? "is-active" : step > item ? "is-done" : ""} onClick={() => goToStep(item)}>
                    <span className="auth-step-number">{step > item ? <i className="bi bi-check-lg" aria-hidden="true" /> : item}</span>
                    <span className="auth-step-label">{item === 1 ? "Tu cuenta" : item === 2 ? "Preferencias" : "Consentimiento"}</span>
                  </button>
                ))}
              </div>
              {searchParams.get("invite") && (
                <Alert variant="success" className="page-feedback">
                  Invitación detectada. Completa el registro para entrar a Cardora.
                </Alert>
              )}
              {error && <Alert variant="danger">{error}</Alert>}
              {isAuth0Enabled ? (
                <Auth0SignupButton returnTo={returnTo} />
              ) : (
                <Form onSubmit={handleSubmit}>
                  {step === 1 && (
                    <Row className="g-3 auth-form-grid">
                    <Col md={6}>
                      <Form.Group controlId="displayName">
                        <Form.Label>Nombre visible</Form.Label>
                        <Form.Control
                          value={form.displayName}
                          onChange={event => setForm({ ...form, displayName: event.target.value })}
                          autoComplete="name"
                          maxLength={100}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="handle">
                        <Form.Label>Usuario</Form.Label>
                        <Form.Control
                          value={form.handle}
                          onChange={event => setForm({ ...form, handle: event.target.value })}
                          isValid={handleStatus === "available"}
                          isInvalid={["invalid", "taken", "error"].includes(handleStatus)}
                          placeholder="tomas_cardora"
                          autoComplete='username'
                          maxLength={40}
                        />
                        {handleStatus === "checking" && <Form.Text className="muted-copy">Comprobando disponibilidad...</Form.Text>}
                        {handleStatus === "available" && <Form.Text className="text-success">Usuario disponible.</Form.Text>}
                        {handleStatus === "taken" && <Form.Control.Feedback type="invalid">Ese usuario ya está registrado.</Form.Control.Feedback>}
                        {handleStatus === "invalid" && <Form.Control.Feedback type="invalid">Usa entre 3 y 40 letras, números, puntos, guiones o guion bajo.</Form.Control.Feedback>}
                        {handleStatus === "error" && <Form.Control.Feedback type="invalid">No pudimos comprobarlo. Intenta nuevamente.</Form.Control.Feedback>}
                        {handleStatus === "idle" && <Form.Text className="muted-copy">Se usa para compartir tu perfil.</Form.Text>}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="email">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={form.email}
                          onChange={event => setForm({ ...form, email: event.target.value })}
                          autoComplete="email"
                          maxLength={254}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="password">
                        <Form.Label>Contraseña</Form.Label>
                        <Form.Control
                          type="password"
                          value={form.password}
                          onChange={event => setForm({ ...form, password: event.target.value })}
                          autoComplete="new-password"
                          minLength={8}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId='confirmPassword'>
                        <Form.Label>Repetir contraseña</Form.Label>
                        <Form.Control type='password' autoComplete='new-password' required value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} isInvalid={Boolean(confirmPassword && confirmPassword !== form.password)} />
                        <Form.Control.Feedback type='invalid'>Las contraseñas no coinciden.</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  )}

                  {step === 2 && (
                  <div className="auth-preferences-box">
                    <section className="auth-registration-section">
                      <div className="auth-section-heading">
                        <span className="auth-section-icon"><i className="bi bi-geo-alt" /></span>
                        <div><h2>Tu ubicación</h2><p>Ayuda a priorizar coleccionistas cercanos, sin limitar tus matches.</p></div>
                      </div>
                      <Row className='g-3'>
                        <Col sm={6}><Form.Group controlId='city'><Form.Label>Ciudad o municipio</Form.Label><Form.Control value={form.city} maxLength={120} autoComplete='address-level2' required onChange={event => setForm({ ...form, city: event.target.value })} /></Form.Group></Col>
                        <Col sm={6}><Form.Group controlId='province'><Form.Label>Provincia <span className="muted-copy">(opcional)</span></Form.Label><ProvinceSelect value={form.province} onChange={event => setForm({ ...form, province: event.target.value })} /></Form.Group></Col>
                        <Col sm={6}><Form.Group controlId='postalCode'><Form.Label>Código postal <span className="muted-copy">(privado y opcional)</span></Form.Label><Form.Control value={form.postalCode} maxLength={5} inputMode='numeric' pattern='[0-9]{5}' autoComplete='postal-code' onChange={event => setForm({ ...form, postalCode: event.target.value.replace(/\D/g, '').slice(0, 5) })} /><Form.Text className="muted-copy">Solo se usa para mejorar la cercanía. Nunca se muestra públicamente.</Form.Text></Form.Group></Col>
                        <Col sm={6}><Form.Group controlId='country'><Form.Label>País</Form.Label><Form.Control value="España" disabled readOnly /></Form.Group></Col>
                      </Row>
                    </section>
                    <section className='auth-registration-section auth-language-section' aria-label="Idiomas de tus cartas">
                      <div className="auth-section-heading">
                        <span className="auth-section-icon"><i className="bi bi-translate" /></span>
                        <div><h2>Idiomas de tus cartas</h2><p>Selecciona los idiomas que coleccionas.</p></div>
                      </div>
                      <div className="auth-language-options">
                        {[['es', 'Español'], ['en', 'Inglés'], ['jp', 'Japonés'], ['other', 'Otros']].map(([code, label]) => (
                          <Form.Check
                            className="auth-language-option"
                            key={code}
                            id={`language-${code}`}
                            label={label}
                            checked={preferences.cardLanguages.includes(code)}
                            onChange={() => setPreferences(current => ({
                              ...current,
                              cardLanguages: current.cardLanguages.includes(code)
                                ? current.cardLanguages.filter(item => item !== code)
                                : [...current.cardLanguages, code]
                            }))}
                          />
                        ))}
                      </div>
                    </section>
                    <section className="auth-registration-section">
                      <div className="auth-section-heading">
                        <span className="auth-section-icon"><i className="bi bi-stars" /></span>
                        <div><h2>Tus intereses</h2><p>Personaliza las sugerencias de cartas, comunidades y eventos.</p></div>
                      </div>
                      <div className="auth-preference-grid">
                        {[
                          ["pokemon", "Pokémon"],
                          ["binder", "Completar binder"],
                          ["trades", "Conectar por cartas"],
                          ["tradeDays", "Trade Days"],
                          ["storeEvents", "Eventos de tiendas"]
                        ].map(([key, label]) => (
                          <Form.Check
                            key={key}
                            type="checkbox"
                            id={`pref-${key}`}
                            label={label}
                            checked={Boolean(preferences[key])}
                            onChange={() => togglePreference(key)}
                          />
                        ))}
                      </div>
                    </section>
                  </div>
                  )}

                  {step === 3 && (
                  <>
                  <div className="auth-consent-intro">
                    <span className="auth-section-icon"><i className="bi bi-shield-check" /></span>
                    <div><h2>Un último paso</h2><p>Revisa cómo quieres participar antes de crear tu cuenta.</p></div>
                  </div>
                  <Form.Check
                    className="legal-check mb-2"
                    type="checkbox"
                    id="acceptedLegal"
                    checked={acceptedLegal}
                    onChange={event => setAcceptedLegal(event.target.checked)}
                    label={<span>Acepto los <Link to="/legal/terms">términos</Link> y la <Link to="/legal/privacy">política de privacidad</Link>.</span>}
                    required
                  />
                  <Form.Check
                    className="legal-check mb-3"
                    type="checkbox"
                    id="acceptedUpdates"
                    checked={acceptedUpdates}
                    onChange={event => setAcceptedUpdates(event.target.checked)}
                    label="Quiero recibir avisos de matches, intereses, comunidades y eventos por email."
                  />
                  <p className="auth-legal-copy">
                    Puedes revisar nuestra <Link to="/legal/cookies">política de cookies</Link>. Las cookies no necesarias se pueden rechazar.
                  </p>
                  </>
                  )}

                  <div className="auth-wizard-actions">
                    {step > 1 && (
                      <Button type="button" variant="outline-dark" disabled={isSubmitting} onClick={() => goToStep(Math.max(1, step - 1))}>
                        Atrás
                      </Button>
                    )}
                    {step < 3 ? (
                      <Button type="button" className="btn-cardora" disabled={step === 1 ? !canContinueStepOne : !canContinueStepTwo} onClick={() => goToStep(Math.min(3, step + 1))}>
                        Continuar
                      </Button>
                    ) : (
                      <Button type="submit" className="btn-cardora" disabled={isSubmitting || !acceptedLegal} aria-busy={isSubmitting}>
                        {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta'}
                      </Button>
                    )}
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default RegisterForm;
